// ── «Потянуть вниз — обновить» на разделах приложения ────────────────────────
// Палец тянет страницу вниз от самого верха (не в окне и не в меню) — под
// шапкой появляется кружок; отпустили, протянув достаточно, — запускается
// onRefresh (синхронизация), кружок крутится, пока она идёт.
//   const ptr = usePullToRefresh(enabled, onRefresh);
//   {enabled && <PullIndicator {...ptr} />}

import { useEffect, useRef, useState } from 'react';
import { t } from '../i18n';
import { usePalette } from '../ui/theme';

const MAX = 100; // дальше кружок не едет
const FIRE = 32; // палец прошёл ~72 px

export function usePullToRefresh(enabled, onRefresh) {
  const [dy, setDy] = useState(0);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const refreshRef = useRef(onRefresh);
  refreshRef.current = onRefresh;
  useEffect(() => {
    if (!enabled) return undefined;
    let startY = 0, curDy = 0, active = false;
    const canPull = () => window.scrollY <= 0 && !document.querySelector('[role="dialog"],[role="menu"]');
    const onTouchStart = (e) => {
      if (!canPull() || !e.touches[0]) return;
      startY = e.touches[0].clientY; active = true; curDy = 0;
    };
    const onTouchMove = (e) => {
      if (!active) return;
      const d = e.touches[0].clientY - startY;
      if (d <= 0) { curDy = 0; setDy(0); return; }
      curDy = Math.min(MAX, d * 0.45);
      setDy(Math.round(curDy));
    };
    const onTouchEnd = () => {
      if (!active) return;
      active = false;
      const fire = curDy >= FIRE;
      setDy(0); curDy = 0; startY = 0;
      if (fire && !busyRef.current) {
        busyRef.current = true; setBusy(true);
        Promise.resolve(refreshRef.current?.()).finally(() => { busyRef.current = false; setBusy(false); });
      }
    };
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      setDy(0);
    };
  }, [enabled]);
  return { dy, busy };
}

export function PullIndicator({ dy, busy }) {
  const C = usePalette();
  if (!(dy > 0 || busy)) return null;
  return (
    <div role="status" aria-label={busy ? t("Синхронизация…") : undefined}
      style={{ position: 'fixed', top: 'var(--ac-top-h, 56px)', left: 'var(--ac-side-w, 0px)', right: 0, zIndex: 95, display: 'flex', justifyContent: 'center',
        transform: `translateY(${busy ? 14 : Math.min(dy, 80) - 36}px)`, transition: busy ? 'transform .15s ease' : 'none', pointerEvents: 'none' }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', background: C.card, boxShadow: '0 2px 12px rgba(0,0,0,.18)',
        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 20, height: 20, border: `2.5px solid ${C.border}`, borderTopColor: C.blue, borderRadius: '50%',
          animation: busy || dy >= FIRE ? 'spin 0.7s linear infinite' : 'none',
          transform: !busy && dy < FIRE ? `rotate(${dy * 8}deg)` : 'none' }} />
      </div>
    </div>
  );
}
